import { ELEMENT_COLORS_HSL, ELEMENT_COLORS } from "../assets/elements.js";
import { getIcon } from "../assets/icons.js";
import { elementFactory } from "./factory.js";
import { chatSubmenu } from "./chatSubmenu.js";

export class tileMenu {
    constructor(containerEl, gameState) {
        
        this.gameState = gameState;
        this.container = containerEl;

        // 1. Sanctuary Submenu Selectors
        this.sanctuaryContainer = this.container.querySelector('.submenu-sanctuary');
        this.sanctuarySubmenu = {
            meditationButton: this.sanctuaryContainer.querySelector('.meditate-button'),
            levelText: this.sanctuaryContainer.querySelector('#info-text-level'),
            mpgainText: this.sanctuaryContainer.querySelector('#info-text-mp'),
            progressFill: this.sanctuaryContainer.querySelector('.progress-fill'),
            progressText: this.sanctuaryContainer.querySelector('.progress-text')
        };
        
        // 2. Hunting Submenu Selectors (Fully Mapped to your new HTML spans)
        this.huntingContainer = this.container.querySelector('.submenu-hunting');
        this.huntingSubmenu = {
            monsterDisplay: this.huntingContainer.querySelector(".hunting-monster-display"),
            monsterName: this.huntingContainer.querySelector(".hunting-monster-name"),
            healthFill: this.huntingContainer.querySelector(".hunting-monster-health-fill"),
            hpCurrent: this.huntingContainer.querySelector(".monster-hp-current"),
            hpMax: this.huntingContainer.querySelector(".monster-hp-max"),
            
            spellSlotsContainer: this.huntingContainer.querySelector(".hunting-spell-slots"),
            usableSpellsContainer: this.huntingContainer.querySelector(".hunting-usable-spells-container"),
            
            actionBtn: this.huntingContainer.querySelector(".hunting-action-button"),
            autoBtn: this.huntingContainer.querySelector(".hunting-auto-button"),
            testingStatDisplay: this.huntingContainer.querySelector(".hunting-tile-stat-display")
        };

        // 3. Quest Submenu Selectors
        this.questContainer = this.container.querySelector(".submenu-quest");
        this.questSubmenu = {
            questTitle: this.questContainer.querySelector(".quest-title"),
            questDescription: this.questContainer.querySelector(".quest-description"),
            questReward: this.questContainer.querySelector(".quest-reward-display"),
            progressContainer: this.questContainer.querySelector(".quest-progress-panel"),
            progressTracker: this.questContainer.querySelector(".quest-progress-tracker"),
            acceptBtn: this.questContainer.querySelector("#quest-accept-btn"),
            claimBtn: this.questContainer.querySelector("#quest-claim-btn"),
        };

        this.updateState = {
            updateMeditationUI: true,
            questStatusChange: true,
        };

        this.chatContainer = this.container.querySelector(".submenu-chat");
        this.chatSubmenu = new chatSubmenu(this.chatContainer);

        this.maxSpellSlots = 8; 
    }
        
    setupTileSubmenus() {
        this.setupSanctuarySubmenu();
        this.setupQuestSubmenu();
        this.setupHuntingSubmenu()
        this.chatSubmenu.init();
    }

    

    setupQuestSubmenu() {
        this.questSubmenu.acceptBtn.addEventListener('click', () => {
            const tile = this.gameState.currentTile;
            if (tile === "empty") return;

            this.gameState.worldHandler.getActiveWorld()?.acceptQuest(tile);
            this.updateQuestSubmenu(true); 
        })

        this.questSubmenu.claimBtn.addEventListener('click', async () => {
            const tile = this.gameState.currentTile;
            if (tile === "empty" || tile.questStatus !== "complete") return;

            this.updateQuestSubmenu(true); 

            try {
                const result = await this.gameState.claimQuestReward(tile.currentQuest.id);
                if (!result) {
                    console.warn("Reward claim was safely bypassed.");
                    return;
                }
            } catch (err) {
                console.error("Failed to process reward:", err.message);
            } finally {
                this.updateQuestSubmenu(true);
            }
        });
    }

    setupHuntingSubmenu() {
        if (this.huntingSubmenu.actionBtn) {
            this.huntingSubmenu.actionBtn.addEventListener('click', () => {
                const tile = this.gameState.currentTile;
                if (!tile || tile === "empty") return;

                // 1. Read the DOM to see what the user equipped
                const hotbarSlots = Array.from(this.huntingSubmenu.spellSlotsContainer.querySelectorAll(".hunting-spell-slot"));
                const equippedElements = hotbarSlots
                    .map(slot => slot.getAttribute("data-assigned-element"))
                    .filter(Boolean); // Cleans out empty slots

                if (equippedElements.length === 0) {
                    console.warn("Hunting aborted: Your active spell queue hotbar is empty!");
                    return;
                }

                // 2. Hand the array of strings directly to the logic handler!
                this.gameState.huntingHandler.executeHuntSequence(equippedElements);

                // 3. Force UI updates after the logic finishes running
                this.updateQuestSubmenu(true); 
                this.updateHuntingSubmenu(true);
            });
        }

        if (this.huntingSubmenu.autoBtn) {
            this.huntingSubmenu.autoBtn.addEventListener('click', () => {
                const handler = this.gameState.huntingHandler;
                
                handler.isAutoHunting = !handler.isAutoHunting;
                
                // Sync the visual button state immediately
                if (handler.isAutoHunting) {
                    this.huntingSubmenu.autoBtn.classList.add('active');
                    
                    // if we just turned Auto ON and nothing is casting, kickstart it!
                    if (!handler.combatQueue.isActive) {
                        const hotbarSlots = Array.from(this.huntingSubmenu.spellSlotsContainer.querySelectorAll(".hunting-spell-slot"));
                        const equippedElements = hotbarSlots.map(slot => slot.getAttribute("data-assigned-element")).filter(Boolean);
                        
                        if (equippedElements.length > 0) {
                            handler.executeHuntSequence(equippedElements);
                        } else {
                            // Failsafe: Turn it back off if the hotbar is empty
                            handler.isAutoHunting = false;
                            this.huntingSubmenu.autoBtn.classList.remove('active');
                            console.warn("Cannot auto-hunt with an empty hotbar.");
                        }
                    }
                } else {
                    this.huntingSubmenu.autoBtn.classList.remove('active');
                }
            });
        }

        this.setupDragAndDrop(); 
        this.setupHotbarRemoval(); 

        this.rebuildHotbarFromSave();

        const animateCastBar = () => {
            this.updateCastVisuals();
            requestAnimationFrame(animateCastBar);
        };

        
        // Kick off the loop
        requestAnimationFrame(animateCastBar);
    }

    

    forceUpdateSubmenu(submenuName) {
        if (submenuName === "hunting") {
            this.updateHuntingSubmenu(true);
        }
    }

    updateUI(uiState) {
        if (uiState.activeTileSubmenu === "hunting") {
            this.updateHuntingSubmenu();
        } else if (uiState.activeTileSubmenu == "chat") {
            this.updateChatSubmenu();
        } else if (uiState.activeTileSubmenu == "sanctuary") {
            this.updateSanctuarySubmenu();
        } else if (uiState.activeTileSubmenu == "quest") {
            this.updateQuestSubmenu();
        }
    }   

    updateQuestSubmenu(forceUpdate = false) {
        const tileInfo = this.gameState.currentTile;

        if (tileInfo === "empty") {
            this.questSubmenu.questTitle.textContent = "nothing";
            this.questSubmenu.questDescription.textContent = "how"
            this.questSubmenu.questReward.textContent = "";
            this.questSubmenu.acceptBtn.disabled = true;
            this.questSubmenu.claimBtn.disabled = true;
            this.questSubmenu.progressContainer.classList.remove("visible");
            return;
        }

        let shouldUpdate = forceUpdate || 
                           this.gameState.uiInfo.hasMoved.quest || 
                           this.updateState.questStatusChange || 
                           this.gameState.uiInfo.questProgressUpdate;

        if (shouldUpdate) {
            this.updateQuestButtons(tileInfo);
            this.updateQuestDescription(tileInfo);
            this.updateQuestProgress(tileInfo);

            this.gameState.uiInfo.hasMoved.quest = false;
            this.updateState.questStatusChange = false;
            this.gameState.uiInfo.questProgressUpdate = false;
        }
    }

    updateQuestButtons(tileInfo) {
        this.questSubmenu.acceptBtn.disabled = (tileInfo.questStatus !== "not-accepted");
        this.questSubmenu.claimBtn.disabled = (tileInfo.questStatus !== "complete");
    }  

    updateQuestDescription(tileInfo) {
        let questInfo = tileInfo.currentQuest;
        
        if (!questInfo) {
            this.questSubmenu.questTitle.textContent = "No Quest Available";
            this.questSubmenu.questDescription.textContent = "";
            this.questSubmenu.questReward.textContent = "";
            return;
        }

        const questString = `Quest ${questInfo.questNum}\nKill ${questInfo.killAmount} ${questInfo.element} element monsters`;
        const rewardString = `${questInfo.reward.amount}x Tier ${questInfo.reward.tier} ${questInfo.reward.element} Spell Shards`;

        this.questSubmenu.questDescription.innerText = questString;
        this.questSubmenu.questReward.innerText = rewardString;

        if (tileInfo.questStatus === "accepted" || tileInfo.questStatus === "claiming") {
            this.questSubmenu.questTitle.style.color = "#ff1";  
        } else if (tileInfo.questStatus === "complete") {
            this.questSubmenu.questTitle.style.color = "#1f1";  
        } else {
            this.questSubmenu.questTitle.style.color = "#fff";  
        }
    }

    updateQuestProgress(tileInfo) {
        if (tileInfo.questStatus !== "not-accepted") {
            let questInfo = tileInfo.currentQuest;
            this.questSubmenu.progressContainer.classList.add("visible");
            
            const progressString = `${questInfo.killProgress}/${questInfo.killAmount} ${questInfo.element} Monsters Killed`;
            this.questSubmenu.progressTracker.innerText = progressString;
        } else {
            this.questSubmenu.progressContainer.classList.remove("visible");
        }
    }

    updateHuntingSubmenu(forceUpdate = false) {
        let shouldUpdate = forceUpdate || 
                this.gameState.uiInfo.hasMoved.hunting || 
                this.gameState.uiInfo.updateHuntingSubmenu;
        
        if (shouldUpdate) {
            const tile = this.gameState.currentTile;
            if (!tile || tile === "empty") return;


            if (this.gameState.uiInfo.hasMoved.hunting || forceUpdate) {
                this.rebuildHotbarFromSave();
            }

            if (this.huntingSubmenu.autoBtn) {
                if (this.gameState.huntingHandler.isAutoHunting) {
                    this.huntingSubmenu.autoBtn.classList.add('active');
                } else {
                    this.huntingSubmenu.autoBtn.classList.remove('active');
                }
            }
           
            

            // 1. Delegate math tracking logic directly to huntingHandler queries
            const element = this.gameState.huntingHandler.getMonsterElement();
            const currentHp = this.gameState.huntingHandler.getMonsterCurrentHp();
            const maxHp = this.gameState.huntingHandler.getMonsterMaxHp();
            const healthPercentage = this.gameState.huntingHandler.getMonsterHealthPercentage();

            // 2. Direct DOM assignments
            if (this.huntingSubmenu.monsterName) {
                this.huntingSubmenu.monsterName.textContent = `1x ${element.toUpperCase()} MONSTER`;
            }

            if (this.huntingSubmenu.hpCurrent && this.huntingSubmenu.hpMax) {
                this.huntingSubmenu.hpCurrent.textContent = Math.floor(currentHp);
                this.huntingSubmenu.hpMax.textContent = Math.floor(maxHp);
                this.huntingSubmenu.healthFill.style.width = `${healthPercentage}%`;
                this.huntingSubmenu.healthFill.style.backgroundColor = ELEMENT_COLORS[element];
            }

            // 3. Spells deck reconstruction loop
            const usableDeckContainer = this.huntingSubmenu.usableSpellsContainer;
            if (usableDeckContainer) {
                usableDeckContainer.innerHTML = ""; 

                const gridSpells = this.gameState.spellHandler.getActiveGridSpells();

                if (gridSpells.length === 0) {
                    usableDeckContainer.innerHTML = `<div style="grid-column: span 6; color: #555; text-align: center; font-size: 0.8rem; margin: auto; user-select: none;">No spells ready.</div>`;
                } else {
                    let spellTiles = elementFactory.createHuntingSpellIcons(gridSpells);
                    spellTiles.forEach(element => {
                        usableDeckContainer.appendChild(element);
                    });
                }
            }

            this.gameState.uiInfo.hasMoved.hunting = false;
            this.gameState.uiInfo.updateHuntingSubmenu = false;
        }
    }

    /**
     * Highly optimized animation loop.
     */
    updateCastVisuals() {
        const queue = this.gameState.huntingHandler.combatQueue;
        
        // 1. EARLY EXIT: If combat is off, and we already painted the idle state, do nothing!
        if (!queue.isActive) {
            if (this.isCastBarIdle) return; // Saves 60 calculations a second!
            this.isCastBarIdle = true;      // Mark as idle so we skip future frames
        } else {
            this.isCastBarIdle = false;     // We are actively casting
        }

        // 2. Iterate over our pre-saved, cached DOM elements (No querySelectorAll!)
        this.cachedHotbarSlots.forEach((slot, index) => {
            const elementName = slot.getAttribute("data-assigned-element");
            if (!elementName) return;

            const targetColor = ELEMENT_COLORS_HSL[elementName]?.color || "#eee";

            if (queue.isActive && index === queue.currentIndex) {
                // Actively Casting: Animate the gradient
                const percent = Math.min(100, (queue.progress / queue.targetDuration) * 100);
                slot.style.setProperty("background", `linear-gradient(to right, ${targetColor} ${percent}%, #333 ${percent}%)`, "important");
                slot.style.setProperty("filter", "grayscale(0%)", "important");
                
            } else if (queue.isActive && index > queue.currentIndex) {
                // Waiting in Queue (Only paint if we just started, CSS handles the rest)
                slot.style.setProperty("background", "#333", "important");
                slot.style.setProperty("filter", "grayscale(50%)", "important");
                
            } else {
                // Finished or Idle
                slot.style.setProperty("background", targetColor, "important");
                slot.style.setProperty("filter", "grayscale(0%)", "important");
            }
        });
    }

    setupDragAndDrop() {
        const deckContainer = this.huntingSubmenu.usableSpellsContainer;
        const slotsContainer = this.huntingSubmenu.spellSlotsContainer;
        const maxSlots = this.maxSpellSlots;

        let draggedSpellId = null;
        let draggedElement = null;
        let floatingIcon = null;
        let currentHoveredSlot = null;

        deckContainer.addEventListener("pointerdown", (event) => {
            const sourceCard = event.target.closest(".hunting-spell");
            if (!sourceCard) return;

            draggedSpellId = sourceCard.id;
            draggedElement = sourceCard.getAttribute("data-element");
            if (!draggedSpellId) return;

            event.preventDefault();

            const activeColor = sourceCard.style.backgroundColor || "#ffffff";
            document.body.style.setProperty('--shard-drag-color', activeColor);

            const nativeIcon = sourceCard.querySelector("svg");
            
            floatingIcon = document.createElement("div");
            floatingIcon.className = "floating-drag-icon";
            floatingIcon.style.left = `${event.clientX}px`;
            floatingIcon.style.top = `${event.clientY}px`;
            
            floatingIcon.style.width = "44px";
            floatingIcon.style.height = "44px";
            floatingIcon.style.backgroundColor = activeColor;
            floatingIcon.style.border = "1px solid #fff";
            floatingIcon.style.position = "fixed";
            floatingIcon.style.zIndex = "99999";
            floatingIcon.style.transform = "translate(-50%, -50%)";
            floatingIcon.style.pointerEvents = "none";

            if (nativeIcon) {
                const vectorClone = nativeIcon.cloneNode(true);
                floatingIcon.appendChild(vectorClone);
            }

            document.body.appendChild(floatingIcon);

            window.addEventListener("pointermove", onPointerMove);
            window.addEventListener("pointerup", onPointerUp);
        });

        const onPointerMove = (event) => {
            if (!floatingIcon) return;

            const padding = 20;
            const clampedX = Math.max(padding, Math.min(event.clientX, window.innerWidth - padding));
            const clampedY = Math.max(padding, Math.min(event.clientY, window.innerHeight - padding));
            
            floatingIcon.style.left = `${clampedX}px`;
            floatingIcon.style.top = `${clampedY}px`;

            const targetSlots = Array.from(slotsContainer.querySelectorAll(".hunting-spell-slot"));
            targetSlots.forEach(slot => slot.classList.remove("drag-over"));

            const ghostRect = floatingIcon.getBoundingClientRect();
            let bestAnchorSlot = null;
            let shortestDistance = Infinity;

            const overlappingSlots = targetSlots.filter(slot => {
                const slotRect = slot.getBoundingClientRect();
                return (
                    ghostRect.left < slotRect.right &&
                    ghostRect.right > slotRect.left &&
                    ghostRect.top < slotRect.bottom &&
                    ghostRect.bottom > slotRect.top
                );
            });

            overlappingSlots.forEach(slot => {
                const slotRect = slot.getBoundingClientRect();
                const distance = Math.hypot(ghostRect.left - slotRect.left, ghostRect.top - slotRect.top);
                if (distance < shortestDistance) {
                    shortestDistance = distance;
                    bestAnchorSlot = slot;
                }
            });

            if (bestAnchorSlot) {
                currentHoveredSlot = bestAnchorSlot;
                currentHoveredSlot.classList.add("drag-over");
            } else {
                currentHoveredSlot = null;
            }
        };

        const onPointerUp = (event) => {
            if (currentHoveredSlot) {
                currentHoveredSlot.classList.remove("drag-over");

                // Paint slot background color matching token signature
                const targetColor = ELEMENT_COLORS_HSL[draggedElement]?.color|| "#eee";
                currentHoveredSlot.style.setProperty("background-color", targetColor, "important");
                currentHoveredSlot.setAttribute("data-assigned-element", draggedElement);
                
                // Clear the slot content container
                currentHoveredSlot.innerHTML = "";
                
                // FIXED: Generate and draw the structural vector icon properly into the slot
                if (draggedElement.toLowerCase() !== "all") {
                    const svgString = getIcon(draggedElement);
                    if (svgString) {
                        currentHoveredSlot.insertAdjacentHTML("beforeend", svgString);
                        const slotSvg = currentHoveredSlot.querySelector("svg");
                        if (slotSvg) {
                            slotSvg.removeAttribute("style");
                            slotSvg.querySelectorAll("path, use, rect, circle").forEach(child => {
                                child.removeAttribute("fill");
                                child.removeAttribute("stroke");
                                child.style.setProperty("fill", "#eee", "important");
                                child.style.setProperty("stroke", "none", "important");
                            });
                        }
                        console.log(targetColor);
                        currentHoveredSlot.style.setProperty("background-color", targetColor, "important");
                    }
                } else {
                    currentHoveredSlot.style.setProperty("background-color", "#eee", "important");
                }

                // --- DYNAMIC EXPANSION GENERATOR PIPELINE ---
                const allSlots = Array.from(slotsContainer.querySelectorAll(".hunting-spell-slot"));
                const currentSlotCount = allSlots.length;
                if (currentHoveredSlot === allSlots[currentSlotCount - 1] && currentSlotCount < maxSlots) {
                    const nextSlotIndex = currentSlotCount + 1;
                    const newSlotNode = document.createElement("div");
                    newSlotNode.className = "hunting-spell-slot";
                    newSlotNode.id = `hunting-spell-slot-${nextSlotIndex}`;
                    slotsContainer.appendChild(newSlotNode);
                }
            }

            const activeElements = Array.from(slotsContainer.querySelectorAll(".hunting-spell-slot"))
                .map(slot => slot.getAttribute("data-assigned-element"))
                .filter(Boolean);
    
            // Cache it into memory instantly so hitting refresh before clicking "Hunt" preserves layout changes!
            this.gameState.huntingHandler.equippedHotbar = activeElements;
            this.cachedHotbarSlots = Array.from(slotsContainer.querySelectorAll(".hunting-spell-slot"));
            if (floatingIcon) {
                floatingIcon.remove();
                floatingIcon = null;
            }

            document.body.style.removeProperty('--shard-drag-color');
            currentHoveredSlot = null;
            draggedSpellId = null;
            draggedElement = null;

            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
        };
    }

    setupHotbarRemoval() {
        const slotsContainer = this.huntingSubmenu.spellSlotsContainer;
        const maxSlots = this.maxSpellSlots;

        // Use event delegation on the hotbar slot wrapper to catch double clicks
        slotsContainer.addEventListener("dblclick", (event) => {
            const clickedSlot = event.target.closest(".hunting-spell-slot");
            if (!clickedSlot) return;

            // Only act if this slot has an element assigned
            const assignedElement = clickedSlot.getAttribute("data-assigned-element");
            if (!assignedElement) return;

            console.log(`Double clicked: clearing slot ${clickedSlot.id}`);

            // 1. Mark this slot as cleared
            clickedSlot.removeAttribute("data-assigned-element");

            // 2. Gather ALL elements currently assigned in order across the hotbar
            const activeElements = Array.from(slotsContainer.querySelectorAll(".hunting-spell-slot"))
                .map(slot => slot.getAttribute("data-assigned-element"))
                .filter(Boolean); // Cleans out the nulls/empty spots

            // 3. Clear the hotbar completely
            slotsContainer.innerHTML = "";

            // 4. Redraw slots for everything that survived the cut
            activeElements.forEach((elementName, index) => {
                const slotNode = document.createElement("div");
                slotNode.className = "hunting-spell-slot";
                slotNode.id = `hunting-spell-slot-${index + 1}`;
                slotNode.setAttribute("data-assigned-element", elementName);

                // Re-apply background HSL color
                const targetColor = ELEMENT_COLORS_HSL[elementName]?.color || "#eee";
                slotNode.style.setProperty("background-color", targetColor, "important");

                // Re-inject vector icon if it's not the "all" profile
                if (elementName.toLowerCase() !== "all") {
                    const svgString = getIcon(elementName);
                    if (svgString) {
                        slotNode.insertAdjacentHTML("beforeend", svgString);
                        const slotSvg = slotNode.querySelector("svg");
                        if (slotSvg) {
                            slotSvg.removeAttribute("style");
                            slotSvg.querySelectorAll("path, use, rect, circle").forEach(child => {
                                child.removeAttribute("fill");
                                child.removeAttribute("stroke");
                                child.style.setProperty("fill", "#eee", "important");
                                child.style.setProperty("stroke", "none", "important");
                            });
                        }
                    }
                } else {
                    slotNode.style.setProperty("background-color", "#eee", "important");
                }

                slotsContainer.appendChild(slotNode);
            });

            // 5. Always append exactly ONE empty slot at the end of the new active list
            if (activeElements.length < maxSlots) {
                const emptySlotNode = document.createElement("div");
                emptySlotNode.className = "hunting-spell-slot";
                emptySlotNode.id = `hunting-spell-slot-${activeElements.length + 1}`;
                slotsContainer.appendChild(emptySlotNode);
            }

            this.cachedHotbarSlots = Array.from(slotsContainer.querySelectorAll(".hunting-spell-slot"));
        });
    }

    rebuildHotbarFromSave() {
        const slotsContainer = this.huntingSubmenu?.spellSlotsContainer;
        if (!slotsContainer) return;

        // Fetch saved string elements (e.g. ['fire', 'water'])
        const savedElements = this.gameState.huntingHandler.equippedHotbar || [];
        const maxSlots = this.maxSpellSlots;

        // 1. Wipe out any default elements currently standing in the container
        slotsContainer.innerHTML = "";

        // 2. Loop through and regenerate everything that survived the save file cut
        savedElements.forEach((elementName, index) => {
            const slotNode = document.createElement("div");
            slotNode.className = "hunting-spell-slot";
            slotNode.id = `hunting-spell-slot-${index + 1}`;
            slotNode.setAttribute("data-assigned-element", elementName);

            // Re-apply background color using your styles engine
            const targetColor = ELEMENT_COLORS_HSL[elementName]?.color || "#eee";
            slotNode.style.setProperty("background-color", targetColor, "important");

            // Re-inject the structural vector icon graphic
            if (elementName.toLowerCase() !== "all") {
                const svgString = getIcon(elementName);
                if (svgString) {
                    slotNode.insertAdjacentHTML("beforeend", svgString);
                    const slotSvg = slotNode.querySelector("svg");
                    if (slotSvg) {
                        slotSvg.removeAttribute("style");
                        slotSvg.querySelectorAll("path, use, rect, circle").forEach(child => {
                            child.removeAttribute("fill");
                            child.removeAttribute("stroke");
                            child.style.setProperty("fill", "#eee", "important");
                            child.style.setProperty("stroke", "none", "important");
                        });
                    }
                }
            } else {
                slotNode.style.setProperty("background-color", "#eee", "important");
            }

            slotsContainer.appendChild(slotNode);
        });

        // 3. Always append exactly ONE empty slot at the very end of our active chain
        if (savedElements.length < maxSlots) {
            const emptySlotNode = document.createElement("div");
            emptySlotNode.className = "hunting-spell-slot";
            emptySlotNode.id = `hunting-spell-slot-${savedElements.length + 1}`;
            slotsContainer.appendChild(emptySlotNode);
        }

        this.cachedHotbarSlots = Array.from(slotsContainer.querySelectorAll(".hunting-spell-slot"));
    }

    updateChatSubmenu() {  }




    //  <div class="submenu-tile submenu-sanctuary">

    //         <div id="sanctuary-title">Sanctuary -- Level <span id="info-text-level">notloaded</span></div>
    //         <div id="sanctuary-amount"><span id="info-text-mp">notloaded</span>MP / Meditation</div>

    //     <div class="progress-bar">
    //         <div class="progress-fill"></div>
    //         <div class="progress-text">notloaded%</div>
    //     </div>
    //         <button class="meditate-button">Meditate</button>
    //         <div class="sanctuary-upgrade-section">a </div>
    //     </div>

    setupSanctuarySubmenu() {
        this.sanctuarySubmenu.meditationButton.addEventListener('click', () => {
            this.gameState.doMeditation();
        })
    }

    updateSanctuarySubmenu() {
        if (this.gameState.uiInfo.updateSanctuarySubmenu) {
            const tile = this.gameState.currentTile;
            console.log(tile);
            console.log(this.sanctuarySubmenu.mpgainText);
            this.sanctuarySubmenu.levelText.innerText = tile.prngSeed;
            this.sanctuarySubmenu.mpgainText.innerText = this.gameState.formatVolume(this.gameState.actions.meditation.mpGain);

            this.gameState.uiInfo.updateSanctuarySubmenu = false;
        } 
    }
}


    //                      QUEST TILE UPDATING LOGIC
    //                       REIMPLEMENT IN WORLD MENU

    // updateHuntingSubmenu(forceUpdate = false) {  
    //     let shouldUpdate = forceUpdate || this.gameState.uiInfo.hasMoved.hunting;
        
    //     if (shouldUpdate) {
    //         const tile = this.gameState.currentTile;
    //         if (!tile || tile === "empty") return;

    //         // Get the active world map handler
    //         const worldHandler = this.gameState.worldHandler.getActiveWorld(); //update
    //         let questInfo = [];
            
    //         //console.log(worldHandler.activeQuests.entries());
    //         // Extract active entries
    //         for (let [questId, tileInstance] of worldHandler.activeQuests.entries()) {
    //             if (tileInstance.currentQuest) {
    //                 let tempTile = tileInstance;
    //                 if (tile.stats.element === tempTile.stats.element || true) {
    //                     let killProg = tempTile.currentQuest.killProgress;
    //                     let killAmt = tempTile.currentQuest.killAmount;
    //                     let questPercentage = Math.min(Math.round((killProg / killAmt) * 100), 100);
    //                     questInfo.push({
    //                         xPos: tempTile.xPos, 
    //                         yPos: tempTile.yPos,
    //                         kills: killProg,
    //                         killMax: killAmt,
    //                         percentage: questPercentage,
    //                         status: tempTile.questStatus
    //                     });
    //                 }
    //             }
    //         }

    //         // Loop through your mapped list and generate HTML tiles
    //         let cardsHTML = "";
    //         if (questInfo.length === 0) {
    //             cardsHTML = `<div class="no-quests-message">No active hunting contracts.</div>`;
    //         } else {
    //             cardsHTML = questInfo.map(quest => {
    //                 return `
    //                     <div class="hunting-quest-tile">
    //                         <div class="quest-tile-header">
    //                             <span class="quest-tile-coords">(${quest.xPos}, ${quest.yPos})</span>
    //                         </div>
    //                         <div class="quest-tile-body">
    //                             <div class="quest-tile-progress-bar">
    //                                 <div class="quest-tile-progress-fill status-${quest.status}" style="width: ${quest.percentage}%"></div>
    //                                 <div class="quest-tile-progress-text">
    //                                     <span>Kills: ${quest.kills} / ${quest.killMax}</span>
    //                                     <span>${quest.percentage}%</span>
    //                                 </div>
    //                             </div>
    //                         </div>
    //                     </div>
    //                 `;
    //             }).join('');
    //         }

    //         // Inject the complete wrapper
    //         this.huntingSubmenu.testingStatDisplay.innerHTML = `
    //             <div class="hunting-tracker-wrapper">
    //                 <div class="tracker-header-title">ACTIVE QUESTS</div>
    //                 <div class="tracker-tiles-list">
    //                     ${cardsHTML}
    //                 </div>
    //             </div>
    //         `;

    //         const huntingText = `HUNT: 1x ${tile.monsters.currentMonster.element} Monster`;
    //         this.huntingSubmenu.monsterDisplay.innerText = huntingText;

    //         this.gameState.uiInfo.hasMoved.hunting = false;
    //     }
    // }
